import { useState } from 'react';

/**
 * Says what happened when Teams tears the app down mid-file-pick.
 *
 * Proven on a Galaxy S10+ on 2026-08-31, build 2026-08-31g: the readout went
 * from `mounts: 1, lastUpload: none` to `mounts: 2, lastUpload: tap
 * auth=authenticated` across one Open-another-document attempt. So the tap
 * landed, auth was fine, the picker opened -- and then the WebView context was
 * destroyed and the app remounted. No `change` event can ever arrive, because
 * the <input> that would carry it no longer exists. The app reloads, reads
 * ?docId= and shows the shared document again, which is what looked for two
 * days like the presenter's document "coming back".
 *
 * Not memory pressure: renderScale read `1.50 @356x503`, about 1.6MB a canvas.
 * Teams appears simply to tear the surface down when an external activity --
 * the Android file chooser -- takes the foreground. Nothing this app does to a
 * plain file input can survive that.
 *
 * So this does not fix opening a document; it stops the failure being silent.
 * A dead-looking button with no explanation cost several days of diagnosis, and
 * would cost a customer the same confusion with no readout to fall back on.
 */
export function PickerInterruptedBanner() {
  // Read once on mount, before anything clears it. The breadcrumb lives in
  // localStorage precisely because it has to outlive the teardown.
  const [interrupted, setInterrupted] = useState<boolean>(() => {
    try {
      const last = window.localStorage.getItem('annoteer.lastUpload') ?? '';
      const mounts = Number(window.localStorage.getItem('annoteer.stageMounts') ?? '0');
      const panelMounts = Number(window.localStorage.getItem('annoteer.panelMounts') ?? '0');
      // A tap is the LAST thing recorded, and we are in a fresh mount: the pick
      // never came back. A completed upload would have overwritten this with
      // `ok …`, and a rejected one with `FAILED …`.
      const wasTap = last.startsWith('tap');
      if (wasTap && mounts + panelMounts > 1) {
        window.localStorage.removeItem('annoteer.lastUpload');
        return true;
      }
    } catch {
      // Storage unavailable: no banner, no crash.
    }
    return false;
  });

  if (!interrupted) return null;

  return (
    <div className="picker-interrupted" role="alert">
      <div className="picker-interrupted__text">
        <strong>Teams closed Annoteer while the file picker was open</strong>, so the file you chose
        was not opened. This is a limitation of Teams on Android. To open your own document, use
        Annoteer in Teams on Windows, macOS, or the web.
      </div>
      <button
        type="button"
        onClick={() => setInterrupted(false)}
        aria-label="Dismiss"
        className="picker-interrupted__close"
      >
        &times;
      </button>
    </div>
  );
}
